<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title class="headline">
        <v-icon left>mdi-file-sign</v-icon>
        {{ $t('docuseal.send_contract_dialog_title') }}
      </v-card-title>

      <v-card-text>
        <v-alert v-if="error" type="error" dismissible @input="error = null">
          {{ error }}
        </v-alert>

        <v-alert v-if="success" type="success" dismissible>
          {{ success }}
        </v-alert>

        <v-form ref="form" v-model="valid">
          <v-select
            v-model="selectedTemplate"
            :items="templates"
            item-text="name"
            item-value="id"
            :label="$t('docuseal.select_template')"
            :rules="[v => !!v || $t('docuseal.template_required')]"
            outlined
            dense
            :loading="loading"
            :disabled="loading || sending"
          >
            <template v-slot:prepend-inner>
              <v-icon small>mdi-file-document</v-icon>
            </template>
          </v-select>

          <v-divider class="my-4"></v-divider>

          <div class="text-subtitle-2 mb-2">{{ $t('docuseal.booking_info') }}:</div>
          <div class="ml-4">
            <div class="text-body-2">
              <strong>ID:</strong> {{ booking.id }}
            </div>
            <div class="text-body-2">
              <strong>{{ $t('docuseal.client') }}:</strong> {{ clientFullName }}
            </div>
            <div class="text-body-2" v-if="booking.client && booking.client.phone">
              <strong>{{ $t('custom.phone') }}:</strong> {{ booking.client.phone }}
            </div>
            <div class="text-body-2" v-if="booking.client && booking.client.email">
              <strong>Email:</strong> {{ booking.client.email }}
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Notification method selection -->
          <div class="text-subtitle-2 mb-2">{{ $t('docuseal.notification_method') }}:</div>
          
          <v-alert v-if="!hasEmailOrPhone" type="warning" dense outlined class="mb-3">
            <div class="text-caption">
              {{ $t('docuseal.no_contact_info') }}
            </div>
          </v-alert>

          <v-radio-group 
            v-model="notificationMethod" 
            :disabled="!hasEmailOrPhone || sending"
            class="mt-2"
          >
            <v-radio 
              value="email" 
              :disabled="!hasEmail"
              :label="hasEmail ? `Email (${booking.client.email})` : $t('docuseal.email_not_available')"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small class="mr-2">mdi-email</v-icon>
                  <span v-if="hasEmail">Email ({{ booking.client.email }})</span>
                  <span v-else class="grey--text">{{ $t('docuseal.email_not_available') }}</span>
                </div>
              </template>
            </v-radio>
            
            <v-radio 
              value="sms" 
              :disabled="!hasPhone"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small class="mr-2">mdi-message-text</v-icon>
                  <span v-if="hasPhone">{{ $t('docuseal.sms') }} ({{ booking.client.phone }})</span>
                  <span v-else class="grey--text">{{ $t('docuseal.phone_not_available') }}</span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-alert type="info" dense outlined class="mt-4">
            <div class="text-caption">
              {{ $t('docuseal.send_info') }}
            </div>
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="close"
          :disabled="sending"
        >
          {{ $t('docuseal.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          @click="sendContract"
          :loading="sending"
          :disabled="!valid || sending"
        >
          <v-icon left>mdi-send</v-icon>
          {{ $t('docuseal.send') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'DocusealContractDialog',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    booking: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      valid: false,
      loading: false,
      sending: false,
      templates: [],
      selectedTemplate: null,
      error: null,
      success: null,
      notificationMethod: 'email' // По умолчанию email
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },

    clientFullName() {
      if (!this.booking.client) return '-'
      
      // Для юрлица используем entity_name, для физлица - ФИО
      if (this.booking.client.entity) {
        return this.booking.client.entity_name || '-'
      }
      
      const { lastname, name, middlename } = this.booking.client
      return [lastname, name, middlename].filter(Boolean).join(' ') || '-'
    },

    hasEmail() {
      return !!(this.booking.client && this.booking.client.email)
    },

    hasPhone() {
      return !!(this.booking.client && this.booking.client.phone)
    },

    hasEmailOrPhone() {
      return this.hasEmail || this.hasPhone
    }
  },

  watch: {
    dialog(val) {
      if (val) {
        this.loadTemplates()
        this.determineNotificationMethod()
      } else {
        this.reset()
      }
    }
  },

  methods: {
    determineNotificationMethod() {
      // Автоматически выбираем метод отправки
      // Если есть email - по умолчанию email
      // Если нет email, но есть телефон - SMS
      if (this.hasEmail) {
        this.notificationMethod = 'email'
      } else if (this.hasPhone) {
        this.notificationMethod = 'sms'
      } else {
        this.notificationMethod = 'email' // Fallback
      }
    },

    loadTemplates() {
      this.loading = true
      this.error = null

      this.axios.get('/api/v1/docuseal/templates', {
        headers: {
          Authorization: this.$store.getters.getAuthToken
        }
      })
      .then(response => {
        this.templates = response.data.templates || []
        if (this.templates.length === 0) {
          this.error = this.$t('docuseal.no_templates')
        }
      })
      .catch(error => {
        console.error('Error loading templates:', error)
        
        const errorMessage = error.response?.data?.error || ''
        
        if (errorMessage.includes('API') || errorMessage.includes('auth') || errorMessage.includes('token')) {
          this.error = this.$t('docuseal.error_invalid_api_key')
        } else if (errorMessage) {
          this.error = errorMessage
        } else {
          this.error = this.$t('docuseal.error_general')
        }
      })
      .finally(() => {
        this.loading = false
      })
    },

    sendContract() {
      if (!this.$refs.form.validate()) {
        return
      }

      // Проверяем наличие контактных данных
      if (!this.hasEmailOrPhone) {
        this.error = this.$t('docuseal.no_contact_info')
        return
      }

      this.sending = true
      this.error = null
      this.success = null
      
      const data = {
        booking_id: this.booking.id,
        template_id: this.selectedTemplate,
        send_sms: this.notificationMethod === 'sms' // Параметр для DocuSeal API
      }

      this.axios.post('/api/v1/docuseal/send_contract', data, {
        headers: {
          Authorization: this.$store.getters.getAuthToken
        }
      })
      .then(response => {
        if (response.data.success) {
          this.success = this.$t('docuseal.send_success')
          // Обновляем список документов бронирования
          this.$emit('contract-sent', response.data)
          
          // Закрываем диалог через 1 секунду после успеха
          setTimeout(() => {
            this.close()
          }, 1000)
        } else {
          const errorMessage = response.data.error || ''
          
          if (errorMessage.includes('API') || errorMessage.includes('auth') || errorMessage.includes('token')) {
            this.error = this.$t('docuseal.error_invalid_api_key')
          } else if (errorMessage) {
            this.error = errorMessage
          } else {
            this.error = this.$t('docuseal.send_error')
          }
        }
      })
      .catch(error => {
        console.error('Error sending contract:', error)
        
        // Детальная обработка ошибок
        const errorMessage = error.response?.data?.error || ''
        
        if (errorMessage.includes('API') || errorMessage.includes('auth') || errorMessage.includes('token')) {
          this.error = this.$t('docuseal.error_invalid_api_key')
        } else if (errorMessage) {
          this.error = errorMessage
        } else {
          this.error = this.$t('docuseal.error_general')
        }
      })
      .finally(() => {
        this.sending = false
      })
    },

    close() {
      this.dialog = false
    },

    reset() {
      this.selectedTemplate = null
      this.error = null
      this.success = null
      this.valid = false
      this.notificationMethod = 'email' // Сброс на значение по умолчанию
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    }
  }
}
</script>

<style scoped>
.text-body-2 {
  margin-bottom: 4px;
}
</style>

